import { generatePath } from 'react-router';
import { makeObservable, observable } from 'mobx';
import { isNumber } from '@cutwise/utils';

import { NOT_FOUND_PAGE, SOLUTIONS_PAGE } from '@/common/constants/pages';
import { assignModel } from '@/common/helpers/assignModel';
import { DriveItemPreview } from '@/drive/entities/DriveItem/DriveItemPreview';
import { DriveItemId } from '@/drive/entities/DriveItemId';
import { PinnedCarbonProject } from '@/forwarding/entities/PinnedItem/PinnedCarbonProject';
import { PinnedItem } from '@/forwarding/entities/PinnedItem/PinnedItem';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { GlobalAccessTarget } from '@/security/entities/GlobalAccessTarget';
import { ProjectDataAccess } from '@/security/types/DataAccess';

import { BaseDriveItem, DriveItemType } from './DriveItem.types';

export class CarbonProjectItemModel {
  id!: DriveItemId;
  title!: string;
  type!: DriveItemType.CarbonProject;
  description?: string;
  preview?: Nullable<DriveItemPreview>;
  canEdit!: boolean;
  canManageAccess!: boolean;
  projectId?: string;
  access?: Nullable<GlobalAccessLevel>;
  dataAccess?: Nullable<ProjectDataAccess>;
}

export class CarbonProjectItem extends BaseDriveItem {
  projectId?: string;
  access?: Nullable<GlobalAccessLevel>;
  dataAccess?: Nullable<ProjectDataAccess>;

  constructor(model: CarbonProjectItemModel) {
    super();

    makeObservable(this, {
      projectId: observable,
      access: observable,
      dataAccess: observable,
    });

    assignModel<CarbonProjectItemModel>(this as unknown as CarbonProjectItemModel, model);
  }

  getLinkData() {
    return {
      isExternal: false,
      url: this.projectId ? generatePath(SOLUTIONS_PAGE, { projectId: this.projectId }) : generatePath(NOT_FOUND_PAGE),
    };
  }

  override canBeShared(): boolean {
    return !!this.projectId;
  }

  override canChangePreview(): boolean {
    return true;
  }

  override toPinnedItem(): Nullable<PinnedItem> {
    if (!this.projectId) {
      return null;
    }

    return new PinnedCarbonProject({ id: this.projectId });
  }

  override toGlobalAccessTarget(): Nullable<GlobalAccessTarget> {
    if (!this.projectId || !isNumber(this.access)) {
      return null;
    }

    return new GlobalAccessTarget({
      id: this.projectId,
      access: this.access,
      dataAccess: this.dataAccess,
      hasDataAccess: true,
    });
  }
}
